import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Repeat,
} from "lucide-react";

interface Transaction {
  createdAt: string;
  userId: string;
  revenueId: string;
  Source: string;
  Destination: string;
  transactionType: string;
  currencyType: string;
  transactionAmount: string;
  cot: string;
  transferFee: string;
  revenue: string;
  grossProfit: string;
  FullName: string;
}

interface TransactionTableProps {
  data?: {
    data: Transaction[];
    page: number;
    pageSize: number;
    total: string;
    totalPages: number;
  };
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount: string, currency: string) => {
  const currencySymbols: Record<string, string> = {
    USD: "$",
    NGN: "₦",
    EUR: "€",
    GBP: "£",
    USDT: "USDT",
    USDC: "USDC",
  };
  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "buy_stablecoin":
      return <ArrowUp className="h-4 w-4 text-[#0eb683]" />;
    case "sell_stablecoin":
      return <ArrowDown className="h-4 w-4 text-orange-500" />;
    case "swap":
      return <Repeat className="h-4 w-4 text-purple-500" />;
    default:
      return null;
  }
};

const toSentenceCase = (text: string) =>
  text
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const RevenueTable = ({
  data,
  isLoading,
  page,
  onPageChange,
}: TransactionTableProps) => {
  const handlePrevPage = () => {
    onPageChange(Math.max(1, page - 1));
  };

  const handleNextPage = () => {
    if (data && page < data.totalPages) {
      onPageChange(page + 1);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-4">Loading...</div>;
  }

  const columns = [
    { key: "sn", label: "S/N", align: "left" },
    { key: "createdAt", label: "Date", align: "left" },
    { key: "FullName", label: "Full Name", align: "left" },
    { key: "transactionType", label: "Type", align: "left" },
    { key: "Source", label: "Source", align: "left" },
    { key: "Destination", label: "Destination", align: "left" },
    { key: "transactionAmount", label: "Amount", align: "right" },
    { key: "cot", label: "COT", align: "right" },
    { key: "transferFee", label: "Transfer Fee", align: "right" },
    { key: "revenue", label: "Revenue", align: "right" },
    { key: "grossProfit", label: "Gross Profit", align: "right" },
  ];

  return (
    <div className="bg-white rounded-lg w-full">
      <div className="overflow-x-auto">
        <table className="appearance-none bg-white min-w-full  mb-6">
          <thead className="bg-[#fff] appearance-none">
            <tr className="border-b bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`font-normal text-sm text-primary py-4  whitespace-nowrap px-4 border-r border-slate-50 ${
                    column.align === "right"
                      ? "text-right"
                      : column.align === "center"
                      ? "text-center"
                      : "text-left"
                  }`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="mt-3 pt-3 w-full space-y-8 border-r border-slate-50">
            {data?.data?.map((transaction, index) => (
              <tr
                key={transaction.revenueId}
                className="appearance-none my-4 border border-slate-50 even:bg-white odd:bg-slate-100"
              >
                <td className="font-normal text-sm text-[#202223] py-4 px-4">
                  {index + 1 + (page - 1) * (data?.pageSize || 0)}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4">
                  {formatDate(transaction.createdAt)}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4">
                  {transaction.FullName}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4 flex items-center gap-2">
                  {getTypeIcon(transaction.transactionType)}
                  {toSentenceCase(transaction.transactionType)}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4">
                  {transaction.Source}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4">
                  {transaction.Destination}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right tabular-nums">
                  {formatCurrency(
                    transaction.transactionAmount,
                    transaction.Source
                  )}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right tabular-nums">
                  {formatCurrency(transaction.cot, transaction.Source)}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right tabular-nums">
                  {formatCurrency(transaction.transferFee, transaction.Source)}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right tabular-nums">
                  {formatCurrency(transaction.revenue, transaction.Source)}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right tabular-nums">
                  {formatCurrency(transaction.grossProfit, transaction.Source)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between p-4">
        <div className="text-sm text-gray-500">
          Page {page} of {data?.totalPages} ({data?.total} total records)
        </div>
        <div className="flex gap-2">
          <button
            className={`flex items-center px-3 py-1 text-sm border rounded-md ${
              page <= 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={handlePrevPage}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </button>
          <button
            className={`flex items-center px-3 py-1 text-sm border rounded-md ${
              !data || page >= data.totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={handleNextPage}
            disabled={!data || page >= data.totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevenueTable;
